import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Copy,
  DollarSign,
  Hash,
  LayoutDashboard,
  Users,
  LogOut,
  ScrollText,
  User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AffiliateDashboard,
  AffiliateReferralCode,
  affiliateApi,
  clearAffiliateSession,
  readAffiliateSession,
} from '../services/affiliateApi';

type Section = 'overview' | 'referrals' | 'codes' | 'payouts' | 'terms' | 'profile' | 'payment';

const money = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
};

const formatMonth = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

function Pill({
  tone,
  children,
}: {
  tone: 'green' | 'amber' | 'red' | 'gray' | 'blue' | 'purple';
  children: React.ReactNode;
}) {
  const tones = {
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-800',
    red: 'bg-red-50 text-red-700',
    gray: 'bg-gray-100 text-gray-600',
    blue: 'bg-blue-50 text-[#0B4778]',
    purple: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-semibold ${tones[tone]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

const AffiliateDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [section, setSection] = useState<Section>('overview');
  const [dashboard, setDashboard] = useState<AffiliateDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [codeInput, setCodeInput] = useState('');
  const [savingCode, setSavingCode] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!readAffiliateSession()) {
      navigate('/affiliates', { replace: true });
      return;
    }
    let cancelled = false;
    affiliateApi
      .getDashboard()
      .then((data) => {
        if (!cancelled) setDashboard(data);
      })
      .catch((error: any) => {
        if (cancelled) return;
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          clearAffiliateSession();
          navigate('/affiliates', { replace: true });
          return;
        }
        setLoadError(error?.response?.data?.message || 'Could not load the affiliate dashboard.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = useMemo(() => {
    const first = dashboard?.affiliate.firstName?.[0] || '';
    const last = dashboard?.affiliate.lastName?.[0] || '';
    return `${first}${last}`.toUpperCase() || 'AP';
  }, [dashboard]);

  const filteredReferrals = useMemo(() => {
    if (!dashboard) return [];
    const query = search.trim().toLowerCase();
    return dashboard.referrals.filter((row) => {
      const stage = row.stage === 'subscription' ? 'subscription' : row.stage === 'trial' ? 'trial' : 'registered';
      const active = row.active ? 'active' : 'inactive';
      const payment = row.payoutStatus === 'not_qualified' ? 'notqualified' : row.payoutStatus;
      const haystack = `${row.businessName} ${row.code}`.toLowerCase();
      return (
        (!query || haystack.includes(query)) &&
        (activeFilter === 'all' || active === activeFilter) &&
        (typeFilter === 'all' || stage === typeFilter) &&
        (paymentFilter === 'all' || payment === paymentFilter)
      );
    });
  }, [dashboard, search, activeFilter, typeFilter, paymentFilter]);

  const updateCode = (next: string) => {
    setCodeInput(next.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
  };

  const createCode = async () => {
    if (!codeInput) {
      toast.error('Enter a referral code first');
      return;
    }
    setSavingCode(true);
    try {
      const created = await affiliateApi.createCode(codeInput);
      setDashboard((current) =>
        current ? { ...current, codes: [created, ...current.codes.filter((item) => item.id !== created.id)] } : current
      );
      setCodeInput('');
      toast.success('Referral code created');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not create that code');
    } finally {
      setSavingCode(false);
    }
  };

  const toggleCode = async (code: AffiliateReferralCode) => {
    const status = code.status === 'active' ? 'paused' : 'active';
    try {
      const updated = await affiliateApi.setCodeStatus(code.id, status);
      setDashboard((current) =>
        current
          ? { ...current, codes: current.codes.map((item) => (item.id === updated.id ? updated : item)) }
          : current
      );
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Could not update that code');
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Referral link copied');
    } catch {
      toast.error('Could not copy the link');
    }
  };

  const logout = () => {
    clearAffiliateSession();
    navigate('/affiliates');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (loadError || !dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-6 text-center">
          <p className="text-sm text-gray-700">{loadError || 'Could not load the affiliate dashboard.'}</p>
          <button type="button" onClick={() => navigate('/affiliates')} className="aacore-btn-primary mt-4 justify-center">
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  const pendingApproval = dashboard.affiliate.approvalStatus === 'pending';
  const activeCodes = dashboard.codes.filter((code) => code.status === 'active').length;
  const navItems: { id: Section; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="h-4 w-4" /> },
    { id: 'referrals', label: 'Referrals', icon: <Users className="h-4 w-4" /> },
    { id: 'codes', label: 'Referral Codes', icon: <Hash className="h-4 w-4" /> },
    { id: 'payouts', label: 'Payouts', icon: <DollarSign className="h-4 w-4" /> },
    { id: 'terms', label: 'Program Terms', icon: <ScrollText className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <header className="sticky top-0 z-20 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between px-4 lg:px-8 h-16">
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <img src="/home/aacore-logo.png" alt="" className="w-10 h-10 rounded-full" />
            <span className="leading-tight">
              <strong className="block text-base font-bold text-[#153e59]">AACore Booking</strong>
              <small className="block text-xs text-[#566977]">Affiliate Program</small>
            </span>
          </Link>
          <div className="relative" ref={accountMenuRef}>
            <button
              type="button"
              onClick={() => setAccountMenuOpen((open) => !open)}
              className="flex items-center p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-300"
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              aria-label="Account menu"
            >
              <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-medium">
                {initials}
              </div>
            </button>
            {accountMenuOpen && (
              <div className="absolute right-0 mt-2 min-w-[12rem] rounded-lg shadow-lg py-2 z-50 border border-gray-200 bg-white">
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {[dashboard.affiliate.firstName, dashboard.affiliate.lastName].filter(Boolean).join(' ')}
                  </p>
                </div>
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      logout();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center gap-3"
                  >
                    <LogOut className="h-5 w-5 text-gray-600 flex-shrink-0" />
                    <span className="whitespace-nowrap">Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className={`bg-white border-b lg:border-b-0 lg:border-r border-gray-200 lg:min-h-[calc(100vh-4rem)] p-3 lg:p-4 flex flex-col ${pendingApproval ? 'opacity-50 pointer-events-none select-none' : ''}`}>
          <div className="hidden lg:block text-sm font-semibold px-2 pb-3 mb-3 border-b border-gray-100">
            Affiliate Dashboard
          </div>
          <div className="flex lg:flex-col gap-1 overflow-auto">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSection(item.id)}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap ${
                  section === item.id ? 'bg-blue-50 text-[#0B4778] font-semibold' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}
            <div className="hidden lg:block text-[10px] uppercase tracking-wider text-gray-400 font-semibold px-3 pt-4 pb-1">
              Account
            </div>
            <button
              type="button"
              onClick={() => setSection('profile')}
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                section === 'profile' ? 'bg-blue-50 text-[#0B4778] font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <User className="h-4 w-4" />
              Partner Profile
            </button>
            <button
              type="button"
              onClick={() => setSection('payment')}
              className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                section === 'payment' ? 'bg-blue-50 text-[#0B4778] font-semibold' : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <DollarSign className="h-4 w-4" />
              Payment Settings
            </button>
          </div>
          <div className="hidden lg:block mt-auto pt-4 border-t border-gray-100 text-xs text-gray-500 leading-relaxed px-2">
            Partner since
            <div className="font-semibold text-gray-700">{formatMonth(dashboard.affiliate.createdAt)}</div>
            <div className="mt-3">Program ID</div>
            <div className="font-semibold text-gray-700">{dashboard.affiliate.programId}</div>
          </div>
        </aside>

        <main className="p-4 sm:p-6 lg:p-8 min-w-0">
          {dashboard.affiliate.approvalStatus === 'pending' && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Your affiliate account is pending administrator approval</p>
                <p className="mt-1 text-sm text-amber-800 leading-relaxed">
                  The rest of this dashboard stays unavailable until an administrator approves your partner account.
                </p>
              </div>
            </div>
          )}
          <div className={pendingApproval ? 'opacity-50 pointer-events-none select-none' : undefined} aria-disabled={pendingApproval}>
          {section === 'profile' && (
            <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 max-w-2xl">
              <h1 className="text-2xl font-semibold text-gray-900">Partner Profile</h1>
              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Name</dt>
                  <dd className="font-medium text-gray-900">
                    {dashboard.affiliate.firstName} {dashboard.affiliate.lastName}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium text-gray-900">{dashboard.affiliate.email}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Program ID</dt>
                  <dd className="font-medium text-gray-900">{dashboard.affiliate.programId}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Partner since</dt>
                  <dd className="font-medium text-gray-900">{formatMonth(dashboard.affiliate.createdAt)}</dd>
                </div>
              </dl>
            </section>
          )}

          {section === 'payment' && (
            <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 max-w-2xl">
              <h1 className="text-2xl font-semibold text-gray-900">Payment Settings</h1>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                Eligible trial bonuses and subscription commission are paid quarterly. Payout history keeps the
                referrals and subscription charges included in each payment. Payment details are confirmed with you
                before the first payout is sent to {dashboard.affiliate.email}.
              </p>
            </section>
          )}

          {(section === 'overview' || section === 'terms' || section === 'codes' || section === 'payouts' || section === 'referrals') && (
            <>
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">Affiliate Dashboard</h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Create referral codes, track referred businesses, and review payment eligibility.
                  </p>
                </div>
                <button type="button" onClick={() => setSection('terms')} className="aacore-btn-outlined hidden sm:inline-flex">
                  View Program Rules
                </button>
              </div>

              {(section === 'overview' || section === 'terms') && (
                <section className="border border-blue-100 bg-white rounded-lg shadow-sm p-5 mb-5 grid gap-4 md:grid-cols-3">
                  <div className="md:col-span-1">
                    <h2 className="text-base font-semibold text-gray-900">AACore Booking Affiliate Program</h2>
                    <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                      Earn a qualified trial bonus plus recurring commission when referred businesses become paying
                      AACore Booking subscribers.
                    </p>
                  </div>
                  <div className="md:border-l md:border-gray-200 md:pl-4">
                    <div className="text-xl font-semibold text-[#0B4778]">$10</div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      per referred business that completes its trial and meets verified business activity requirements.
                    </p>
                  </div>
                  <div className="md:border-l md:border-gray-200 md:pl-4">
                    <div className="text-xl font-semibold text-purple-700">15%</div>
                    <p className="text-xs text-gray-500 leading-relaxed">
                      of eligible subscription payments for that business during its first 12 subscription months. Paid quarterly.
                    </p>
                  </div>
                </section>
              )}

              {(section === 'overview' || section === 'payouts') && (
                <section className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
                  <Metric label="Total Referred Businesses" value={String(dashboard.metrics.referredBusinesses)} foot={`${dashboard.metrics.activeBusinesses} currently active`} />
                  <Metric label="Qualified Trial Bonuses" value={money(dashboard.metrics.trialBonusCents)} foot={`${dashboard.metrics.qualifiedTrialCount} verified referrals`} good />
                  <Metric label="Subscription Commission" value={money(dashboard.metrics.commissionCents)} foot="Across active subscriptions" />
                  <Metric label="Next Eligible Payout" value={money(dashboard.metrics.nextPayoutCents)} foot={`${dashboard.metrics.payoutQuarterLabel} payout cycle`} />
                </section>
              )}

              {(section === 'overview' || section === 'codes' || section === 'payouts') && (
                <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr)_minmax(280px,0.8fr)] gap-4 mb-5">
                  {(section === 'overview' || section === 'codes') && (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm min-w-0">
                      <div className="px-4 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                        <div>
                          <h2 className="text-base font-semibold">Referral Codes</h2>
                          <p className="text-xs text-gray-500 mt-1">
                            Create unique codes for campaigns or partner outreach. Codes cannot contain spaces or special characters and are limited to 12 characters.
                          </p>
                        </div>
                        <Pill tone="blue">{activeCodes} Active</Pill>
                      </div>
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row gap-2 mb-2">
                          <div className="relative flex-1">
                            <input
                              value={codeInput}
                              maxLength={12}
                              autoComplete="off"
                              placeholder="ENTER NEW CODE"
                              onChange={(event) => updateCode(event.target.value)}
                              className="aacore-textfield-primary uppercase tracking-wide pr-16"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                              {codeInput.length} / 12
                            </span>
                          </div>
                          <button type="button" onClick={createCode} disabled={savingCode} className="aacore-btn-primary justify-center">
                            {savingCode ? 'Creating...' : 'Create Code'}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mb-3">Letters and numbers only. The code must be unique across AACore Booking.</p>
                        <div className="space-y-2">
                          {dashboard.codes.length === 0 && (
                            <p className="text-sm text-gray-500">No referral codes yet.</p>
                          )}
                          {dashboard.codes.map((code) => (
                            <div key={code.id} className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-sm font-bold tracking-wide text-gray-900">{code.code}</span>
                                  {code.status === 'active' ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Paused</Pill>}
                                </div>
                                <div className="text-xs text-gray-500 truncate">{code.link.replace(/^https?:\/\//, '')}</div>
                              </div>
                              <div className="flex gap-2">
                                <button type="button" onClick={() => copyLink(code.link)} className="aacore-btn-outlined text-xs">
                                  <Copy className="h-3.5 w-3.5" />
                                  Copy Link
                                </button>
                                <button type="button" onClick={() => toggleCode(code)} className="aacore-btn-outlined text-xs">
                                  {code.status === 'active' ? 'Pause' : 'Activate'}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {(section === 'overview' || section === 'payouts') && (
                    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="px-4 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                        <div>
                          <h2 className="text-base font-semibold">Quarterly Payout</h2>
                          <p className="text-xs text-gray-500 mt-1">{dashboard.metrics.payoutQuarterLabel} earnings currently eligible for payment.</p>
                        </div>
                        <Pill tone="amber">Pending</Pill>
                      </div>
                      <div className="p-4">
                        <div className="flex items-end justify-between pb-4 mb-3 border-b border-gray-100">
                          <div>
                            <div className="text-xs text-gray-500">Eligible Amount</div>
                            <div className="text-2xl font-semibold">{money(dashboard.metrics.nextPayoutCents)}</div>
                          </div>
                          <span className="text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2 py-1">Quarterly</span>
                        </div>
                        <PayoutRow label="Qualified trial bonuses" value={money(dashboard.metrics.trialBonusCents)} />
                        <PayoutRow label="Subscription commission" value={money(dashboard.metrics.commissionCents)} />
                        <PayoutRow label="Adjustments" value={money(0)} />
                        <PayoutRow label="Expected payout" value={money(dashboard.metrics.nextPayoutCents)} total />
                        <p className="mt-3 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-3 leading-relaxed">
                          <strong className="text-gray-700">Payment status:</strong> Pending quarterly close. Payout history preserves the underlying referrals and subscription charges included in each payment.
                        </p>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {(section === 'overview' || section === 'referrals') && (
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm mb-5">
                  <div className="px-4 py-4 border-b border-gray-100 flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold">Referred Businesses</h2>
                      <p className="text-xs text-gray-500 mt-1">
                        Track registration, trial qualification, subscription status, commission term, and payout eligibility.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="aacore-btn-outlined"
                      onClick={() => {
                        setSearch('');
                        setActiveFilter('all');
                        setTypeFilter('all');
                        setPaymentFilter('all');
                      }}
                    >
                      Reset Filters
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2 p-3 border-b border-gray-100">
                    <input
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search business or code"
                      className="aacore-textfield-primary"
                    />
                    <select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)} className="aacore-textfield-primary">
                      <option value="all">All Statuses</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)} className="aacore-textfield-primary">
                      <option value="all">Trial + Subscription</option>
                      <option value="trial">Trial</option>
                      <option value="subscription">Subscription</option>
                    </select>
                    <select value={paymentFilter} onChange={(event) => setPaymentFilter(event.target.value)} className="aacore-textfield-primary">
                      <option value="all">All Eligibility</option>
                      <option value="eligible">Eligible</option>
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="notqualified">Not Qualified</option>
                    </select>
                  </div>
                  <div className="overflow-auto">
                    <table className="w-full min-w-[960px] text-left">
                      <thead>
                        <tr className="text-[11px] uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-100">
                          {['Business', 'Referral Code', 'Status', 'Stage', 'Trial Bonus', 'Subscription Term', '15% Commission', 'Payout Status'].map((heading) => (
                            <th key={heading} className="px-4 py-3 font-semibold">{heading}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredReferrals.length === 0 && (
                          <tr>
                            <td colSpan={8} className="px-4 py-8 text-sm text-gray-500 text-center">
                              No referred businesses match these filters.
                            </td>
                          </tr>
                        )}
                        {filteredReferrals.map((row) => (
                          <tr key={row.id} className="border-b border-gray-100 last:border-0">
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold text-gray-900">{row.businessName}</div>
                              <div className="text-xs text-gray-500">Registered {formatDate(row.registeredAt)}</div>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold">{row.code}</td>
                            <td className="px-4 py-3">{row.active ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Inactive</Pill>}</td>
                            <td className="px-4 py-3">
                              {row.stage === 'subscription' ? <Pill tone="purple">Subscription</Pill> : <Pill tone="blue">{row.stage === 'trial' ? 'Trial' : 'Registered'}</Pill>}
                            </td>
                            <td className="px-4 py-3"><TrialBonus row={row} /></td>
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {row.subscriptionMonth ? `Month ${row.subscriptionMonth} of 12` : 'Starts after paid subscription'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-sm font-semibold">{money(row.commissionCents)}</div>
                              <div className="text-xs text-gray-500">Lifetime earned</div>
                            </td>
                            <td className="px-4 py-3"><PayoutPill status={row.payoutStatus} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {section === 'overview' && (
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="px-4 py-4 border-b border-gray-100">
                    <h2 className="text-base font-semibold">Referral Eligibility Flow</h2>
                    <p className="text-xs text-gray-500 mt-1">How a referred business moves from signup to a quarterly payout.</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 p-4">
                    {[
                      ['Registered', 'Business signs up with an affiliate code.'],
                      ['Trial Active', 'Referral is tracked during the trial period.'],
                      ['Activity Verified', 'Required business activity is confirmed.'],
                      ['$10 Eligible', 'Trial completed and the referral bonus is earned.'],
                      ['Subscription', '15% accrues on eligible payments for months 1 through 12.'],
                      ['Quarterly Paid', 'Eligible bonus and commission are attached to a payout record.'],
                    ].map(([title, copy], index) => (
                      <div key={title}>
                        <div className="w-8 h-8 rounded-full bg-blue-50 text-[#0B4778] text-xs font-bold grid place-items-center border border-blue-100">
                          {index + 1}
                        </div>
                        <div className="text-sm font-semibold mt-2">{title}</div>
                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">{copy}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
          </div>
        </main>
      </div>
    </div>
  );
};

function Metric({ label, value, foot, good }: { label: string; value: string; foot: string; good?: boolean }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <div className="text-xs text-gray-500 mb-2">{label}</div>
      <div className="text-2xl font-semibold tracking-tight">{value}</div>
      <div className={`text-xs mt-1 ${good ? 'text-green-700' : 'text-gray-500'}`}>{foot}</div>
    </div>
  );
}

function PayoutRow({ label, value, total }: { label: string; value: string; total?: boolean }) {
  return (
    <div className={`flex justify-between gap-4 py-2 text-sm ${total ? 'border-t border-gray-100 mt-2 pt-3 font-semibold' : 'text-gray-600'}`}>
      <span>{label}</span>
      <strong className="text-gray-900">{value}</strong>
    </div>
  );
}

function TrialBonus({ row }: { row: AffiliateDashboard['referrals'][number] }) {
  if (row.trialBonusStatus === 'qualified') return <Pill tone="green">Qualified</Pill>;
  if (row.trialBonusStatus === 'paid') return <Pill tone="gray">Paid</Pill>;
  if (row.trialBonusStatus === 'not_qualified') return <Pill tone="red">Did Not Qualify</Pill>;
  return <Pill tone="amber">Verification Pending</Pill>;
}

function PayoutPill({ status }: { status: string }) {
  if (status === 'eligible') return <Pill tone="green">Eligible</Pill>;
  if (status === 'paid') return <Pill tone="gray">Paid</Pill>;
  if (status === 'not_qualified') return <Pill tone="red">Not Qualified</Pill>;
  return <Pill tone="amber">Pending</Pill>;
}

export default AffiliateDashboardPage;
